<template>
  <v-card>
    <v-toolbar flat>
      <v-toolbar-title>
        Ansvar
      </v-toolbar-title>
      <v-spacer />
      <v-menu
        v-model="addMenu"
        left
        :close-on-content-click="false"
        min-width="500"
      >
        <template #activator="{ attrs, on }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            small
          >
            Nyt ansvar
            <v-icon right> mdi-plus </v-icon>
          </v-btn>
        </template>
        <v-card>
          <v-card-title>
            Nyt ansvar
          </v-card-title>
          <v-card-text>
            <v-form
              ref="form"
              v-model="addForm"
            >
              <v-text-field
                v-model="addItem.title"
                label="Navn"
                autofocus
                :rules="[ v => !!v || 'Udfyld et navn' ]"
                outlined
              />
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn
              text
              @click="clear"
            >
              {{ $t('common.cancel') }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              :disabled="!addForm"
              :loading="creating"
              @click="create"
            >
              Opret
              <v-icon right> mdi-plus </v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </v-toolbar>
    <v-data-table
      :items="items"
      :headers="headers"
      sort-by="sortOrder"
    >
      <template #[`item.isAdmin`]="{ value }">
        <v-chip :color="value ? 'success' : 'default'" label small>
          {{ value ? 'Ja' : 'Nej' }}
        </v-chip>
      </template>
      <template #[`item.permissions`]="{ item, value }">
        <v-chip-group multiple>
          <v-chip v-for="permission in value.slice(0, 5)" :key="permission" small label>
            {{ permission }}
          </v-chip>

          <v-chip v-if="value.length > 5" label small @click="onEdit(item)">
            ... {{ value.length - 5 }} flere
          </v-chip>
        </v-chip-group>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn
          small
          @click="onEdit(item)"
        >
          <v-icon>mdi-pencil-outline</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog
      v-model="editDialog"
      width="800"
    >
      <v-card>
        <v-card-title>
          Rediger ansvar
        </v-card-title>
        <v-card-text>
          <v-form
            ref="editForm"
            v-if="editItem"
            v-model="editForm"
          >
            <v-text-field
              v-model="editItem.title"
              label="Navn"
              :rules="[v => !!v || 'Udfyld titel']"
              outlined
            />
            <v-text-field
              v-model="editItem.sortOrder"
              type="number"
              label="Sortering"
              outlined
              clearable
            />
            <v-select
              v-model="editItem.customerDeliveryEditorOrganizationKinds"
              :items="organizationKinds"
              label="Organisationer der må ændre tildelt"
              hint="Brugere fra disse organisationer må ændre hvem der har fået tildelt dette ansvar på sager"
              persistent-hint
              item-text="label"
              small-chips
              deletable-chips
              multiple
              outlined
            />
            <v-autocomplete
              v-model="editItem.permissions"
              :items="permissions"
              label="Rettigheder"
              item-value="id"
              item-text="title"
              clearable
              auto-select-first
              outlined
              multiple
              small-chips
              deletable-chips
              hint="Vær opmærksom på hvad du gør!"
              persistent-hint
            />
            <v-checkbox
              v-model="editItem.isAdmin"
              label="Administrator"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            :disabled="!editForm"
            :loading="saving"
            @click="save"
          >
            {{ $t('common.save') }}
            <v-icon right>
              mdi-content-save-outline
            </v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
const baseUrl = 'responsibilities';

export default {
  name: 'ResponsibilitiesComponent',
  data() {
    return {
      creating: false,
      loading: true,
      saving: false,
      addItem: {},
      addMenu: false,
      addForm: false,
      editDialog: false,
      editItem: null,
      editForm: false,
      items: [],
      permissions: [],
      organizationKinds: [
        {
          label: 'Intern',
          value: 'Internal'
        },
        {
          label: 'Kunde',
          value: 'Customer'
        },
        {
          label: 'Leverandør',
          value: 'Contractor'
        }
      ],
      headers: [
        { text: 'Navn', value: 'title', width: 300 },
        { text: 'Administrator', value: 'isAdmin', width: 150 },
        { text: 'Rettigheder', value: 'permissions' },
        { text: 'Sortering', value: 'sortOrder', align: 'right' },
        { text: 'Actions', value: 'actions', width: 100, align: 'right', sortable: false }
      ]
    }
  },
  mounted: async function() {
    await this.getItems();
    this.getPermissions()
  },
  watch: {
    editDialog(value) {
      if (!value) {
        this.editItem = null;
      }
    }
  },
  methods: {
    getItems: async function() {
      this.loading = true;

      try {
        const { data } = await this.$api.get(baseUrl, {
          params: {
            customerId: this.$route.params.customerId
          }
        });

        this.items = data;
        this.$emit('loaded', data);
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.loading = false;
      }
    },
    getPermissions: async function() {
      const { data } = await this.$api.get(baseUrl + '/permissions');

      this.permissions = data;
    },
    create: async function() {
      this.creating = true;

      const model = {
        ...this.addItem,
        customerId: this.$route.params.customerId
      };

      try {
        const { data } = await this.$api.post(baseUrl, model);
        if (!data.success) throw data.errors?.at(0)?.message ?? data.message ?? 'Something went wrong';

        this.$notifier.showSuccess({ message: 'Ansvar oprettet' });
        this.addMenu = false;
        this.addItem = {};
        this.getItems();
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.creating = false;
      }
    },
    save: async function() {
      this.saving = true;

      try {
        const { data } = await this.$api.patch(`${baseUrl}/${this.editItem.id}`, this.editItem);

        if (!data.success) throw data.errors?.at(0)?.message ?? data.message ?? 'Something went wrong';

        this.$notifier.showSuccess({ message: 'Ansvar opdateret' });
        this.getItems();
        this.editDialog = false;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.saving = false;
      }
    },
    onEdit: function(item) {
      this.editItem = JSON.parse(JSON.stringify(item));
      this.editDialog = true;
    },
    clear: function() {
      this.addItem = {};
      this.addMenu = false;
    }
  }
}
</script>